import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Constants } from '@app/common/constants';
import { HttpHelper } from '@app/common/http-helper';
import { GraphBatchData, GraphBatchRequest } from '@app/models/graph-batch-request';
import { PersonExtended } from '@app/models/person-extended';
import { User as UserModel } from '@app/models/user';
import { CacheService } from '@app/services/cache.service';
import { Person, User } from '@microsoft/microsoft-graph-types';
import { concat, forkJoin, iif, Observable, of, Subject, throwError } from 'rxjs';
import { catchError, map, shareReplay, takeUntil, tap } from 'rxjs/operators';
import { FeatureSettingsStoreService } from './feature-settings-store.service';

@Injectable({
  providedIn: 'root'
})
export class UserService {
  constructor(private http: HttpClient, private cache: CacheService, private featureStoreSettingsService: FeatureSettingsStoreService, private httpHelper: HttpHelper) { }

  private readonly cacheKeyPrefix = 'UserService';

  public useCache = true;

  private getMyDetailsFromMsGraph$: Observable<User>;
  private getUserPhotoByUpn$ = new Map<string, Observable<string>>();
  private getUserByUpn$ = new Map<string, Observable<User>>();
  private getSingleUserByUpn$ = new Map<string, Observable<User>>();
  private getOrCreateMe$: Observable<UserModel>;
  private reload$ = new Subject<void>();
  private getUserPhotoOfBlobByUpn$ = new Map<string, Observable<Blob>>();
  private defaultSelectProperties = 'id,userPrincipalName,displayName,givenName,surname,businessPhones,mobilePhone,displayName,postalCode,jobTitle,department,officeLocation,mail,department,state,city,proxyAddresses,postalAddress,birthday,streetAddress';

  public reload() {
    this.reload$.next();
    this.getOrCreateMe$ = null;
    if (this.useCache) {
      const cacheKey = `${this.cacheKeyPrefix}-getOrCreateMe`;
      this.cache.removeFromPersistentCache(cacheKey);
    }
  }

  getOrCreateMe(): Observable<UserModel> {
    if (this.getOrCreateMe$) {
      return this.getOrCreateMe$;
    } else {
      const cacheKey = `${this.cacheKeyPrefix}-getOrCreateMe`;
      this.getOrCreateMe$ = this.http.get<UserModel>(`${Constants.API_ENDPOINT}/users/me`)
        .pipe(map(user => {
          if (user) {
            if (user.ShowNewsFromFollowedSites === null) {
              user.ShowNewsFromFollowedSites = true;
            }
            this.useCache && this.cache.set(cacheKey, user);
            return user;
          }
        }),
          catchError(err => {
            return iif(() =>
              err.status === 404,
              this.http.post<UserModel>(`${Constants.API_ENDPOINT}/users`, new UserModel({ ShowNewsFromFollowedSites: true, }))
                .pipe(map(user => {
                  this.useCache && this.cache.set(cacheKey, user);
                  return user;
                })),
              throwError(err)
            );
          }),
          catchError(this.httpHelper.handleError({ operation: 'getOrCreateMe', result: null, rethrow: true, cacheKey: cacheKey })),
          takeUntil(this.reload$),
          shareReplay({ bufferSize: 1, refCount: true }));

      if (this.useCache) {
        const cachedData = this.cache.get<UserModel>(cacheKey);
        if (cachedData) {
          return concat(of(cachedData), this.getOrCreateMe$);
        }
      }
      return this.getOrCreateMe$;
    }
  }

  updateMe(user: UserModel): Observable<boolean> {
    const fetchOperation = this.http.put<UserModel>(`${Constants.API_ENDPOINT}/users/`, user, {

    })
      .pipe(
        map(response => {
          // invalidate cache
          this.reload();
          return true;
        }),
        catchError(this.httpHelper.handleError({ operation: 'updateMe', result: false, rethrow: true })));

    return fetchOperation;
  }

  getUserPhotoByUpn(userPrincipalName: string): Observable<string> {
    if (this.getUserPhotoByUpn$.has(userPrincipalName)) {
      return this.getUserPhotoByUpn$.get(userPrincipalName);
    } else {
      const apiEndPoint = `${Constants.GRAPH_API_ENDPOINT}${Constants.GRAPH_API_VERSION_ONE}/users/${userPrincipalName}/photo/$value`;

      this.getUserPhotoByUpn$.set(userPrincipalName, this.http.get(apiEndPoint, {
        responseType: 'blob',
      })
        .pipe(map(result => {
          return URL.createObjectURL(result);
        }),
          catchError(err => {
            if (err.status === 404) {
              return of(null);
            } else {
              return throwError(err);
            }
          }),
          shareReplay({ bufferSize: 1, refCount: true })));
      return this.getUserPhotoByUpn$.get(userPrincipalName);
    }
  }

  // This method returns blob response from graph api which is used in the api to write on the user cv
  getUserPhotoOfBlobByUpn(userPrincipalName: string): Observable<Blob> {
    if (this.getUserPhotoOfBlobByUpn$.has(userPrincipalName)) {
      return this.getUserPhotoOfBlobByUpn$.get(userPrincipalName);
    } else {
      const apiEndPoint = `${Constants.GRAPH_API_ENDPOINT}${Constants.GRAPH_API_VERSION_ONE}/users/${userPrincipalName}/photo/$value`;

      this.getUserPhotoOfBlobByUpn$.set(userPrincipalName, this.http.get(apiEndPoint,
        { responseType: 'blob' })
        .pipe(map(result => {
          return result;
        }),
          catchError(err => {
            if (err.status === 404) {
              return of(null);
            } else {
              return throwError(err);
            }
          }),
          shareReplay({ bufferSize: 1, refCount: true })));
      return this.getUserPhotoOfBlobByUpn$.get(userPrincipalName);
    }
  }

  getSingleUserByUpn(userPrincipalName: string): Observable<Person> {
    if (this.getSingleUserByUpn$.has(userPrincipalName)) {
      return this.getSingleUserByUpn$.get(userPrincipalName);
    } else {
      const httpParams = new HttpParams()
        .append('$select', this.defaultSelectProperties);

      const apiEndPoint = `${Constants.GRAPH_API_ENDPOINT}${Constants.GRAPH_API_BETA_VERSION}/users/${userPrincipalName}`;

      this.getSingleUserByUpn$.set(userPrincipalName, this.http.get<any>(apiEndPoint, { params: httpParams })
        .pipe(
          map(response => {
            return response as Array<Person>;
          }),
          catchError(err => {
            if (err.status === 404) {
              return of(null);
            } else {
              return throwError(err);
            }
          }),
          shareReplay({ bufferSize: 1, refCount: true })));

      return this.getSingleUserByUpn$.get(userPrincipalName);
    }
  }

  getUserByUpn(userPrincipalName: string): Observable<User> {
    if (this.getUserByUpn$.has(userPrincipalName)) {
      return this.getUserByUpn$.get(userPrincipalName);
    } else {
      const apiEndPoint = `${Constants.GRAPH_API_ENDPOINT}${Constants.GRAPH_API_VERSION_ONE}/users/${userPrincipalName}`;
      this.getUserByUpn$.set(userPrincipalName,
        this.http.get(apiEndPoint, {
          responseType: 'json',
        })
          .pipe(
            map(result => {
              return result as User;
            }),
            catchError(err => {
              if (err.status === 404) {
                return this.searchMsGraphForUsers(userPrincipalName, 1)
                  .pipe(
                    map(results => {
                      if (results?.length > 0) {
                        return results[0];
                      } else {
                        return null;
                      }
                    })
                  );
              } else {
                return throwError(err);
              }
            }),
            shareReplay({ bufferSize: 1, refCount: true })));
      return this.getUserByUpn$.get(userPrincipalName);
    }
  }

  /*
 * Queries MS Graph for multiple user photos.
 */
  getBatchUserPhotoByUpn(userPrincipalNames: Array<string>): Observable<Array<string>> {

    if (userPrincipalNames.length === 0) {
      return of(userPrincipalNames);
    }

    const data = {} as GraphBatchData;
    data.requests = userPrincipalNames.map((upn, index) => {
      return {
        id: index.toString(),
        method: 'GET',
        url: `/users/${upn}/photo/$value`,
        headers: {
          'responseType': 'blob'
        },
      } as GraphBatchRequest;
    });

    const fetchOperation = this.http.post<any>(`${Constants.GRAPH_API_ENDPOINT + Constants.GRAPH_API_VERSION_ONE}/$batch`, data)
      .pipe(
        map(response => {
          return response.responses.map(r => {
            const upn = userPrincipalNames[r.id];

            if (r.status.toString().startsWith('4')) {
              return upn;
            }

            return `data:image/png;base64,${r.body}`;
          });
        }),
        catchError(this.httpHelper.handleError({ operation: 'getBatchUserPhotoByUpn', result: ({} as Array<string>), rethrow: true })));

    return fetchOperation;
  }

  /** Batch get user entries by UPN */
  getBatchUserByUpn(userPrincipalNames: Array<string>): Observable<Array<Person>> {

    // remove empty entries
    if (userPrincipalNames) {
      userPrincipalNames = userPrincipalNames.filter(f => f);
    }

    // abort if null
    if (!userPrincipalNames || !userPrincipalNames.length) {
      return of([]);
    }

    // limit for each batch request is 20 names
    const chunkSize = 20;
    const allRequests: Observable<Array<Person>>[] = [];
    for (let i = 0, j = userPrincipalNames.length; i < j; i += chunkSize) {
      const currentUpnChunk = userPrincipalNames.slice(i, i + chunkSize);
      allRequests.push(this.getBatchUserByUpnWorker(currentUpnChunk));
    }

    return forkJoin(allRequests).pipe(map(allResults => allResults.reduce((a, b) => [...a, ...b], [])));
  }

  /** Actual worker method for  getBatchUserByUpn, note that limit is 20 names. */
  private getBatchUserByUpnWorker(userPrincipalNames: Array<string>): Observable<Array<Person>> {

    // since this is a private function called by the above call function, this should never exceed 20 upns
    // if someone calls it wrongly, log error
    if (userPrincipalNames.length > 20) {
      userPrincipalNames = userPrincipalNames.slice(0, 20);
      console.error('getBatchUserByUpnWorker called with moe than 20 names, will only process 20x');
    }

    const data = {} as GraphBatchData;
    data.requests = userPrincipalNames.map((upn, index) => {
      return {
        id: index.toString(),
        method: 'GET',
        url: `/users/${upn}`,
        headers: {
          'responseType': 'json'
        },
      } as GraphBatchRequest;
    });

    return this.http.post<any>(`${Constants.GRAPH_API_ENDPOINT + Constants.GRAPH_API_VERSION_ONE}/$batch`, data)
      .pipe(
        map(response => {
          return response.responses.map(r => {
            const upn = userPrincipalNames[r.id];

            if (r.status.toString().startsWith('4')) {
              return {
                displayName: upn,
                mail: upn
              };
            }

            return r.body as Person;
          });
        }),
        catchError(this.httpHelper.handleError({ operation: 'getBatchUserByUpn', result: ({} as Array<string>), rethrow: true })));
  }

  /*
   * Queries MS Graph for details about the signed in user.
   * Only specified properties are returned. Add more if you
   * need, see: https://docs.microsoft.com/en-us/graph/api/resources/user?view=graph-rest-1.0
   * For reference about return values for assignedPlans and assignedLicenses see https://docs.microsoft.com/en-us/azure/active-directory/users-groups-roles/licensing-service-plan-reference
   */
  getMyDetailsFromMsGraph(noCache?: boolean): Observable<User> {
    if (this.getMyDetailsFromMsGraph$) {
      return this.getMyDetailsFromMsGraph$;
    } else {
      const cacheKey = `${this.cacheKeyPrefix}-myDetails`;
      const properties = this.featureStoreSettingsService.state.myDetailsMsGraphProperties.join(',');

      this.getMyDetailsFromMsGraph$ = this.http
        .get<any>(`${Constants.GRAPH_API_ENDPOINT + Constants.GRAPH_API_VERSION_ONE}/me/?$select=${properties}`)
        .pipe(
          map(response => {
            const result = response as User;
            // console.log(`fetched current user details: `, result);
            return result;
          }),
          tap((data) => {
            this.useCache && this.cache.set(cacheKey, data);
          }),
          catchError(this.httpHelper.handleError({ operation: 'getMyDetailsFromMsGraph', result: ({} as User), rethrow: true, cacheKey: cacheKey })),
          shareReplay({ bufferSize: 1, refCount: true })
        );

      if (this.useCache && !noCache) {
        const cachedData = this.cache.get<User>(cacheKey);
        if (cachedData) {
          return concat(of(cachedData), this.getMyDetailsFromMsGraph$);
        }
      }

      return this.getMyDetailsFromMsGraph$;
    }
  }

  getUserFromMsGraph(upn: string): Observable<User> {
    const cacheKey = `${this.cacheKeyPrefix}-getUserFromMsGraph-${upn}`;

    const fetchOperation = this.http
      .get<any>(`${Constants.GRAPH_API_ENDPOINT + Constants.GRAPH_API_VERSION_ONE}/users/${upn}`)
      .pipe(
        map(response => {
          const result = response as User;
          return result;
        }),
        tap((data) => {
          this.useCache && this.cache.set(cacheKey, data);
        }),
        catchError(this.httpHelper.handleError({ operation: 'getUserFromMsGraph', result: ({} as User), rethrow: true, cacheKey: cacheKey })),
        shareReplay({ bufferSize: 1, refCount: true })
      );

    if (this.useCache) {
      const cachedData = this.cache.get<User>(cacheKey);
      if (cachedData) {
        return of(cachedData);
      }
    }

    return fetchOperation;

  }

  /*
  * Search for users using MS Graph and comma separated keywords (each having a graph call of its own)
  * Each user returned will contain information of which keyword(s) was matched in PersonExtended.SearchTermHits
  */
  searchMsGraphForUsersWithMultipleKeywords(keywords: string, max: number): Observable<Array<PersonExtended>> {

    // If there is only a single search term, search normally using the parameter 'max'
    if (keywords.indexOf(',') === -1) {
      const t = this.searchMsGraphForUsersWithKeyword(keywords, max);
      return t;
    }

    // If there is more than one search term, upp the max count to the supported max (999). TODO: implement pagination
    // This is so that we have a better chance of combining a correct 'AND' match later on (the graph results will be combined with results from our CV database)
    const multiKeyWordMax = 999;

    const searchTerms = keywords.split(',');
    const uniqueUsers: PersonExtended[] = [];
    const graphCalls = searchTerms.map(keyword => this.searchMsGraphForUsersWithKeyword(keyword.trim(), multiKeyWordMax));
    return forkJoin([...graphCalls]).pipe(
      map(resultSets => {
        resultSets.forEach(userList => {

          userList.forEach(user => {

            // if the user is already in the unique list of users then only update the SearchTermHits
            const matchingUser = uniqueUsers.find(u => {
                if (u.userPrincipalName && user.userPrincipalName) {
                  return u.userPrincipalName.toLowerCase() === user.userPrincipalName.toLowerCase();
                }
                return false;
              });
            if (matchingUser) {
              user.SearchTermHits.forEach(term => {
                if (matchingUser.SearchTermHits.indexOf(term) === -1) {
                  matchingUser.SearchTermHits.push(term);
                }
              });
            } else {

              // user not already in the list, add it
              uniqueUsers.push(user);
            }
          });
        });

        uniqueUsers.sort((x, y) => y.SearchTermHits.length - x.SearchTermHits.length);
        // note that we don't trim down to 'max' number of hits here
        // This is on purpose so that we have a better chance of combining a correct 'AND' match later on (this result will be combined with results from our CV database)
        return uniqueUsers;
      })
    );
  }

  /*
  * Search for users using MS Graph
  * cast to PersonExtended and include the search term used in the result
  */
  searchMsGraphForUsersWithKeyword(keyword: string, max: number): Observable<Array<PersonExtended>> {

    // use searchMsGraphForUser but cast to PersonExtended so we can set the 'SearchTermHits' property. This is so that we can do a proper 'AND' matching between keywords later on.
    return this.searchMsGraphForUsers(keyword, max)
      .pipe(
        map(results => {
          // cast to PersonExtended
          const users = results as Array<PersonExtended>;
          users.map(user => {
            user.SearchTermHits = [keyword];
            return user;
          });
          return users;
        }));
  }

  /*
  * Search for users using MS Graph
  */
  searchMsGraphForUsers(keyword: string, max: number): Observable<Array<User>> {

    // abort if no keyword provided
    if (!keyword || !keyword.trim()) {
      return of([]);
    }

    // Note: MS Graph does not have free form search on
    // users objects, so until this is available we use
    // multiple startsWith fitlers
    const filter = `accountEnabled eq true and (` +
      `startswith(displayName,'${keyword}') or startswith(givenName,'${keyword}') or startswith(surname,'${keyword}') or startswith(mail,'${keyword}') or startswith(userPrincipalName,'${keyword}')` +
      ` or startswith(city, '${keyword}')` +
      ` or startswith(department, '${keyword}')` +
      ` or startswith(jobTitle, '${keyword}')` +
      ` or startswith(state, '${keyword}')` +
      `)` + // close the and clause in "accountEnabled eq true and (" here
      this.featureStoreSettingsService.state.userServiceAppendedFilter; // Allows adding additional clauese to the filter, by default this is an empty string

    const httpParams = new HttpParams()
      .append('$filter', filter)
      .append('$top', `${max}`);

    const fetchOperation = this.http.get<any>(`${Constants.GRAPH_API_ENDPOINT + Constants.GRAPH_API_BETA_VERSION}/users`, { params: httpParams })
      .pipe(
        map(response => {
          const result = response.value as Array<User>;
          return result;
        }),
        catchError(this.httpHelper.handleError({ operation: 'searchMsGraphForUsers', result: new Array<Person>(), rethrow: true}))
      );
    return fetchOperation;
  }

  /*
  * Search for related people using MS Graph
  */
  getRelatedPeople(top: number): Observable<Array<Person>> {

    const filter = 'personType/class eq \'Person\' and personType/subclass eq \'OrganizationUser\'' + this.featureStoreSettingsService.state.userServiceAppendedFilter;
    const cacheKey = `${this.cacheKeyPrefix}-getRelatedPeople-${top}`;
    const httpParams = new HttpParams()
      .append('$top', top.toString())
      .append('$filter', filter);

    const fetchOperation = this.http.get<any>(`${Constants.GRAPH_API_ENDPOINT + Constants.GRAPH_API_VERSION_ONE}/me/people`,
      { params: httpParams })
      .pipe(
        map(response => {
          return response.value as Array<Person>;
        }),
        tap(data => {
          this.useCache && this.cache.set(cacheKey, data);
        }),
        catchError(this.httpHelper.handleError({ operation: 'getRelatedPeople', result: new Array<Person>(), rethrow: true, cacheKey: cacheKey })));

    if (this.useCache) {
      const cachedData = this.cache.get<Array<User>>(cacheKey);
      if (cachedData) {
        return concat(of(cachedData), fetchOperation);
      }
    }
    return fetchOperation;
  }

  /*
   * Figures out if the user is licensed to use specified O365 services
   * Checks assignedLicenses (subscriptions) and assignedPlans (individual licenses?) on the graph user object
   * Pass arrays with GUIDs, first required licenses then licenses that are disallowed
   * For assignedPlans the "String ID" can be used as well. This is however not available for subscriptions.
   * OR logic is used when passing more than one license
   * Rejected licenses take presidence over required ones
   * Resources to find GUIDs and String IDs:
   *   In graph explorer (https://developer.microsoft.com/en-us/graph/graph-explorer) use query: https://graph.microsoft.com/v1.0/me/?$select=assignedLicenses,assignedPlans
   *   MS doc: https://docs.microsoft.com/en-us/azure/active-directory/users-groups-roles/licensing-service-plan-reference
   */
  isUserLicensed(requiredLicenses: string[], rejectedLicenses: string[]): Observable<Boolean> {

    if (requiredLicenses.length === 0 && rejectedLicenses.length === 0) {
      return of(true);
    }

    return this.getMyDetailsFromMsGraph().pipe(
      map(user => {
        const enabledAssignedPlans = user.assignedPlans.filter(plan => plan.capabilityStatus === 'Enabled');
        const licenses =
          user.assignedLicenses.map(d => d.skuId.toLowerCase())
            .concat(enabledAssignedPlans.map(d => d.servicePlanId.toLowerCase()))
            .concat(enabledAssignedPlans.map(d => d.service.toLowerCase()));

        const rejected = rejectedLicenses.map(d => d.toLowerCase());
        const matchingRejectedLicenses = rejected.filter(element => licenses.includes(element));
        if (matchingRejectedLicenses.length > 0) {
          return false;
        }

        if (requiredLicenses.length === 0) {
          return true;
        }

        const required = requiredLicenses.map(d => d.toLowerCase());
        const matchingRequiredLicenses = required.filter(element => licenses.includes(element));
        return matchingRequiredLicenses.length > 0;
      })
    );

  }

}
